import { Col, Flex, Row, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { useFetchBoardViewerConfig } from 'business/quality-control/hooks/use-fetch-board-viewer-config';
import { RingBoardViewer } from 'business/quality-control/pages/defect-report-history/components/ring-board-viewer';
import { RingComplianceStatus } from 'business/quality-control/pages/ring-compliance-report/hooks/data/types';
import Routes from 'config/routes';
import { RingValidationDetailV1OutputDto } from 'generated/apiSchemas';
import { formatDateWithMiddleDot } from 'technical/format/date';
import { PreviousPageButton } from 'ui/button/previous-page-button';

import styles from './index.module.scss';
import { InvalidateRingButton } from './invalidate-ring-button';
import { RingNavigationButton } from './navigation-buttons';
import { PrintRingButton } from './print-ring-button';
import { RingComplianceStatusTag } from './ring-compliance-status-tag';
import { StatusFrame } from './status-frame';
import { ValidateRingButton } from './validate-ring-button';

export const RingComplianceHeader = ({
  detail,
  ringSegments,
}: {
  detail: RingValidationDetailV1OutputDto;
  ringSegments: { label: string }[];
}) => {
  const { t } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const {
    segmentDrawConfigs,
    segmentWidth,
    tunnelPositions,
    defectReportTypes,
    isLoading: isLoadingBoardConfig,
    error: boardConfigError,
  } = useFetchBoardViewerConfig(currentConstructionSite.id);

  const location = useLocation();
  const backTo =
    (location.state as { backTo?: string } | null)?.backTo ??
    Routes.QualityRingComplianceReport;

  const {
    modules: {
      QUALITY_CONTROL: { isConstructionSiteUser },
    },
  } = usePermissions();
  const keyPositionPrefix = currentConstructionSite?.keyPositionPrefix ?? '';
  const heading =
    detail.keyPosition != null
      ? t('qualityControl.ringComplianceReport.detail.headingWithKeyPosition', {
          number: detail.ringNumber,
          keyPosition: `${keyPositionPrefix}${detail.keyPosition}`,
        })
      : t('qualityControl.ringComplianceReport.detail.heading', {
          number: detail.ringNumber,
        });

  const showCompliance = [
    RingComplianceStatus.Compliant,
    RingComplianceStatus.Approved,
    RingComplianceStatus.Refused,
  ].includes(detail.status as RingComplianceStatus);
  const showApproval = detail.status === RingComplianceStatus.Approved;
  const showRefusal = detail.status === RingComplianceStatus.Refused;

  return (
    <Flex vertical gap={16}>
      <Row align="middle" className={styles.topControls}>
        <Col span={10}>
          <PreviousPageButton
            to={backTo}
            label="common.go_back"
            isMobile={false}
          />
        </Col>

        <Col span={4}>
          <Flex gap={8} justify="center">
            <RingNavigationButton
              direction="previous"
              ring={detail.previousRing}
              backTo={backTo}
            />
            <RingNavigationButton
              direction="next"
              ring={detail.nextRing}
              backTo={backTo}
            />
          </Flex>
        </Col>

        <Col span={10}>
          <Flex gap={8} justify="end">
            <PrintRingButton
              ringStatus={detail.status}
              ringNumber={detail.ringNumber}
              isValidationActivated={
                currentConstructionSite.isQualityValidatorActivated
              }
              hasPdf={detail.pdfBucketFileId !== null}
            />
            {detail.status === RingComplianceStatus.Compliant &&
              isConstructionSiteUser &&
              currentConstructionSite.isQualityValidatorActivated && (
                <>
                  <InvalidateRingButton
                    ringId={detail.id}
                    segments={ringSegments}
                  />
                  <ValidateRingButton ringNumber={detail.ringNumber} />
                </>
              )}
          </Flex>
        </Col>
      </Row>
      <Flex vertical align="center" gap={8}>
        <Typography.Title
          level={3}
          style={{ margin: 0 }}
          className={styles.heading}
        >
          {heading}
        </Typography.Title>
        <Typography.Text type="secondary">
          {t('qualityControl.ringComplianceReport.detail.buildDate', {
            datetime: detail.buildDate
              ? formatDateWithMiddleDot(detail.buildDate)
              : '-',
          })}
        </Typography.Text>
        <RingComplianceStatusTag status={detail.status} />
      </Flex>

      <div className={styles.boardWrapper}>
        <RingBoardViewer
          ringNumber={detail.ringNumber}
          segmentDrawConfigs={segmentDrawConfigs}
          segmentWidth={segmentWidth}
          tunnelPositions={tunnelPositions}
          defectReportTypes={defectReportTypes}
          isLoadingBoardConfig={isLoadingBoardConfig}
          boardConfigError={boardConfigError}
        />
      </div>

      {showCompliance && (
        <Flex gap={12} className={styles.complianceSection}>
          {detail.complianceDate && (
            <StatusFrame
              status={RingComplianceStatus.Compliant}
              date={detail.complianceDate}
              authorName={detail.complianceAuthorName}
            />
          )}
          {showApproval && detail.approvalDate && (
            <StatusFrame
              status={RingComplianceStatus.Approved}
              date={detail.approvalDate}
              authorName={detail.approvalAuthorName}
            />
          )}
          {showRefusal && detail.refusalDate && (
            <StatusFrame
              status={RingComplianceStatus.Refused}
              date={detail.refusalDate}
              authorName={detail.refusalAuthorName}
              description={detail.description ?? '-'}
              refusedSegments={detail.refusedSegments}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};
